import type { FormikErrors, FormikTouched } from "formik";

export { DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE } from "./userFacingMessages";

function isFieldTouched<T extends object>(
    touched: FormikTouched<T> | undefined,
    field: keyof FormikErrors<T>
): boolean {
    if (!touched) return false;
    const value = touched[field as keyof FormikTouched<T>];
    return value === true || (typeof value === "object" && value !== null);
}

function hasFieldError<T extends object>(errors: FormikErrors<T>, field: keyof FormikErrors<T>): boolean {
    return Boolean(errors[field]);
}

/**
 * Form grid helper: when `showAfterSubmitAttempt` is true (e.g. submitCount > 0), show all
 * schema errors; otherwise show only touched fields with errors.
 */
export function shouldShowFormFieldError<T extends object>(
    field: keyof T & string,
    errors: FormikErrors<T>,
    touched: FormikTouched<T>,
    showAfterSubmitAttempt: boolean
): boolean;

/**
 * Edit page helper: after submitCount > 0, show all schema errors; persisted records and
 * live-validation modes show errors as the user edits; otherwise touched fields only.
 */
export function shouldShowFormFieldError<T extends object>(
    errors: FormikErrors<T>,
    field: keyof FormikErrors<T>,
    hasPersistedRecord: boolean,
    submitCount: number,
    liveValidationAfterSubmit: boolean,
    touched?: FormikTouched<T>
): boolean;

export function shouldShowFormFieldError<T extends object>(
    arg1: FormikErrors<T> | (keyof T & string),
    arg2: keyof FormikErrors<T> | FormikErrors<T>,
    arg3: boolean | FormikTouched<T>,
    arg4?: number | boolean,
    arg5?: boolean,
    arg6?: FormikTouched<T>
): boolean {
    if (typeof arg1 === "string") {
        const field = arg1;
        const errors = arg2 as FormikErrors<T>;
        const touched = arg3 as FormikTouched<T>;
        const showAfterSubmitAttempt = arg4 as boolean;
        if (showAfterSubmitAttempt) return hasFieldError(errors, field);
        return Boolean(isFieldTouched(touched, field) && errors[field]);
    }

    const errors = arg1 as FormikErrors<T>;
    const field = arg2 as keyof FormikErrors<T>;
    const hasPersistedRecord = arg3 as boolean;
    const submitCount = arg4 as number;
    const liveValidationAfterSubmit = arg5 as boolean;
    const touched = arg6;

    if (submitCount > 0) return hasFieldError(errors, field);
    if (hasPersistedRecord || liveValidationAfterSubmit) return hasFieldError(errors, field);
    return Boolean(isFieldTouched(touched, field) && errors[field]);
}
